// @flow
import React, { useEffect, useState } from 'react';
import Button from 'antd/lib/button';
import styled from 'styled-components';
import { Selects, WaypointsForm } from './../../../components';
import { vehicleStatusEnum, waypointTypeEnum } from '../../../lib/enum';
import type { Trip, Vehicle } from '../../../lib/types';
import { Section, SectionTitle, Panel } from './../../../components/layout';
import Grid, { GridItem } from './../../../components/layout/Grid';
import { convertFromHourToSecond, convertFromKmToM, convertFromMToKm, convertFromSecondToHour, getPathWithHistoryParams, navigate } from '../../../lib/helpers';
import CancelButton from '../../../components/CancelButton';
import Header from '../../../components/layout/Header';
import Breadcrumbs, { Crumb } from './../../../components/layout/Breadcrumbs';
import Form, { RequiredFieldMessage } from '../../../components/Form';
import Input from 'antd/lib/input';
import { routeApi, tripApi, vehicleApi } from '../../../lib/api';
import { vehicleTypes, ownerTypes } from '../../../lib/enum';
import { WorkAccountingTypes } from '../../../lib/types/vehicleModel';
import { getVehicleGroup } from '../../VehicleModels/lib';
import { InputNumber } from './VerificationForm.elements';
import { connect } from 'react-redux';
import { Profile } from '../../../ducks/auth';
import { AppState } from '../../../ducks/redux';
import { WayPoint } from '../../../lib/types';
import { Route } from '../../../lib/types';
import { directions } from '../../../lib/gis';
import notification from 'antd/lib/notification';
import { notificationLoading } from '../../../components/Notifications';

const StyledPanel = styled(Panel)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 0;
`;

const { VehicleSelect, EmployeeSelect, YesNoSelect } = Selects;

const Footer = styled(Section)`
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Content = styled.div`
  padding: 16px;
`;

type Props = {
  profile: Profile[]
};

const CreateTripListForm = ({ profile }: Props) => {
  const trip: Trip = {
    toOtherSide: true
  };

  const [id, setId] = useState<string | null>(null);
  const [vehicle, setVehicle] = useState<Vehicle | null>(null);

  const getNewId = async () => {
    const id = await tripApi.getNewTripNumber();
    setId(id);
  };

  const createTrip = async (values: Trip) => {
    const route: Route = {
      waypoints: values.expectedRoute.waypoints,
      distance: parseInt(values.currentOdometerAtEnd - values.currentOdometerAtStart) || 0
    };
    const expectedRoute = await routeApi.addRoute(route);
    const trip: Trip = {
      ...values,
      expectedRoute: expectedRoute,
      expectedRouteId: expectedRoute.id,
      startDate: expectedRoute.startDate,
      endDate: expectedRoute.endDate,

      currentOdometerAtStart: convertFromKmToM(values.currentOdometerAtStart),
      currentOdometerAtEnd: convertFromKmToM(values.currentOdometerAtEnd),

      vehicleMachineHoursAtStart: convertFromHourToSecond(values.vehicleMachineHoursAtStart),
      vehicleMachineHoursAtEnd: convertFromHourToSecond(values.vehicleMachineHoursAtEnd),
      currentVehicleMachineHoursAtStart: convertFromHourToSecond(values.vehicleMachineHoursAtStart),
      currentVehicleMachineHoursAtEnd: convertFromHourToSecond(values.vehicleMachineHoursAtEnd),
    };

    return await tripApi.addVerifiedTrip(trip);
  };

  const onChooseVehicle = async (
    setFieldValue: (string, string) => void,
    value: string
  ) => {
    setFieldValue('odometerAtEnd', undefined);
    setFieldValue('workHoursPerTrip', undefined);

    setFieldValue('vehicleId', value);
    setFieldValue('orgUnitId', profile.employeeOrgUnitId);

    const vehicle = await vehicleApi.fetchVehicle(value);
    setVehicle(vehicle);
    setFieldValue('vehicle', vehicle);
    setFieldValue('vehicleType', vehicle.vehicleModel.type);
    setFieldValue('licensePlate', vehicle.licensePlate);
    setFieldValue('vehicleGroup', getVehicleGroup(vehicle.vehicleModel.type));

    if (
      vehicle.vehicleModel.workAccountingType !==
      WorkAccountingTypes.workHours
    ) {
      setFieldValue('currentOdometerAtStart', vehicle.kilometrageByOdometr ?? 0);
      setFieldValue('currentOdometerAtEnd', vehicle.kilometrageByOdometr ?? 0);
    }
    if (
      vehicle.vehicleModel.workAccountingType !== WorkAccountingTypes.kilometrage
    ) {
      setFieldValue(
        'vehicleMachineHoursAtStart',
        vehicle.engineWorkHours ?? 0
      );
      setFieldValue('vehicleMachineHoursAtEnd', vehicle.engineWorkHours ?? 0);
    }
  };

  useEffect(() => {
    getNewId();
  }, []);

  return (
    <Form
      initialValues={trip}
      validate={values => {
        const errors = {};
        if (
          values.expectedRoute &&
          (!values.expectedRoute.waypoints ||
            values.expectedRoute?.waypoints.length < 2)
        ) {
          errors.expectedWaypoints = new RequiredFieldMessage(
            'Маршрут обязателен для заполнения'
          );
        }
        return errors;
      }}
      onSubmit={async (values, actions) => {
        try {
          notificationLoading({
            message: 'Сохранение данных...',
            key: 'saving'
          });
          const trip = await createTrip(values);
          notification.success({
            message: 'Успешно сохранено',
            description: 'Изменения успешно сохранены'
          });
          navigate(`/trips/self/${trip.id}/card`);
        } catch (error) {
          notification.error({
            message: 'Ошибка',
            description: error.message
          });
        } finally {
          notification.close('saving');
        }
      }}
    >
      {(
        FormField: FormFieldType,
        {
          setFieldValue,
          handleSubmit,
          values,
          dirty,
          isSubmitting,
          errors,
        }: FormikProps
      ) => (
        <form onSubmit={handleSubmit}>
          <Header
            left={
              <Breadcrumbs>
                <Crumb to={getPathWithHistoryParams('/trips/self')}>
                  Путевые листы собственных ТС
                </Crumb>
                <Crumb>Таксировка</Crumb>
              </Breadcrumbs>
            }
          />
          <StyledPanel>
            <h1>Таксировка</h1>
          </StyledPanel>
          <StyledPanel>
            <p>Путевой лист {id}-а</p>
          </StyledPanel>
          <Section>
            <SectionTitle divider>Транспортное средство</SectionTitle>
            <Content>
              <Grid gutter="16px" cols={5}>
                <GridItem span={2}>
                  <FormField
                    label="Государственный регистрационный знак ТС"
                    name="licensePlate"
                    required
                  >
                    {field => (
                      <VehicleSelect
                        name={field.name}
                        optionTitle={(vehicle: Vehicle) => vehicle.licensePlate}
                        value={field.value}
                        onChange={value =>
                          onChooseVehicle(setFieldValue, value)
                        }
                        allowClear={false}
                        filter={{
                          ownerType: [ownerTypes.self, ownerTypes.leased],
                          nodeId: profile.employeeBranchOrgUnitId,
                          onlyWithLicensePlate: true,
                          status: [
                            vehicleStatusEnum.draft,
                            vehicleStatusEnum.onAgreeing,
                            vehicleStatusEnum.working,
                            vehicleStatusEnum.sendingToParus,
                            vehicleStatusEnum.onRepair,
                            vehicleStatusEnum.limitExceed
                          ]
                        }}
                      />
                    )}
                  </FormField>
                </GridItem>
                <GridItem>
                  <FormField label="Тип ТС" name="vehicleType">
                    {field => (
                      <Input
                        name={field.name}
                        value={field.value ? vehicleTypes[field.value] : ''}
                        disabled
                      />
                    )}
                  </FormField>
                </GridItem>

                <GridItem>
                  <FormField
                    label="Марка ТС"
                    name="vehicle.vehicleModel.brandName"
                  >
                    {field => (
                      <Input name={field.name} value={field.value} disabled />
                    )}
                  </FormField>
                </GridItem>
                <GridItem>
                  <FormField label="На сторону" name="toOtherSide">
                    {field => (
                      <YesNoSelect
                        onChange={(value: boolean) =>
                          setFieldValue(field.name, value)
                        }
                        name={field.name}
                        value={field.value}
                      />
                    )}
                  </FormField>
                </GridItem>
              </Grid>
            </Content>
            <Content>
              <Grid gutter="16px">
                <GridItem fullWidth>
                  <FormField
                    name="expectedRoute.waypoints"
                    validateStatus={errors.expectedWaypoints && 'error'}
                    help={errors.expectedWaypoints}
                    required
                  >
                    {field => (
                      <WaypointsForm
                        waypoints={field.value}
                        editTypes={[
                          waypointTypeEnum.transit,
                          waypointTypeEnum.end
                        ]}
                        hasErrors={!!errors[field.name]}
                        onChange={(waypoints: WayPoint[]) => {
                          setFieldValue(field.name, waypoints);
                        }}
                        disableDates={false}
                      />
                    )}
                  </FormField>
                </GridItem>
              </Grid>
            </Content>
            {vehicle &&
              !!vehicle.vehicleModel.workAccountingType &&
                vehicle.vehicleModel.workAccountingType !== WorkAccountingTypes.workHours && (
                <>
                  <SectionTitle divider>Пробег</SectionTitle>
                  <Content>
                    <Grid cols={2} gutter="16px">
                      <GridItem>
                        <FormField
                          label="При выезде из гаража"
                          name="currentOdometerAtStart"
                        >
                          {field => (
                            <Input
                              name={field.name}
                              value={field.value}
                              disabled
                            />
                          )}
                        </FormField>
                      </GridItem>
                      <GridItem>
                        <FormField
                          label="При заезде в гараж"
                          name="currentOdometerAtEnd"
                        >
                          {field => (
                            <Input
                              name={field.name}
                              value={field.value}
                              disabled
                            />
                          )}
                        </FormField>
                      </GridItem>
                      <GridItem>
                        <FormField
                          label="Работник, снявший показания"
                          name="odometerReaderEmployeeId"
                          required
                        >
                          {field => (
                            <EmployeeSelect
                              onChange={value => {
                                setFieldValue(field.name, value);
                              }}
                              value={field.value}
                            />
                          )}
                        </FormField>
                      </GridItem>
                      <GridItem>
                        <FormField label="Примечание" name="monitoringNotation">
                          {field => (
                            <Input
                              name={field.name}
                              value={field.value}
                              onChange={field.onChange}
                            />
                          )}
                        </FormField>
                      </GridItem>
                      <GridItem>
                        <FormField
                          label="Пробег за рейс"
                          name="odometerAtEnd"
                          required
                        >
                          {field => (
                            <InputNumber
                              name={field.name}
                              min={0}
                              value={field.value ? convertFromMToKm(field.value) : ''}
                              onChange={value => {
                                setFieldValue(field.name, convertFromKmToM(value));
                                setFieldValue(
                                  'currentOdometerAtEnd',
                                  Number(Number(values.currentOdometerAtStart ?? '0') + value).toFixed(2)
                                );
                              }}
                              decimalSeparator=","
                            />
                          )}
                        </FormField>
                      </GridItem>
                    </Grid>
                  </Content>
                </>
              )}
            {vehicle &&
              vehicle.vehicleModel.workAccountingType !==
                WorkAccountingTypes.kilometrage && (
                <>
                  <SectionTitle divider>Маш. часы</SectionTitle>
                  <Content>
                    <Grid gutter="16px" cols={3}>
                      <GridItem>
                        <FormField
                          label="При выезде из гаража"
                          name="vehicleMachineHoursAtStart"
                        >
                          {field => (
                            <Input
                              name={field.name}
                              value={field.value}
                              disabled
                            />
                          )}
                        </FormField>
                      </GridItem>
                      <GridItem>
                        <FormField
                          label="При заезде в гараж"
                          name="vehicleMachineHoursAtEnd"
                        >
                          {field => (
                            <Input
                              name={field.name}
                              value={field.value}
                              disabled
                            />
                          )}
                        </FormField>
                      </GridItem>
                      <GridItem>
                        <FormField
                          label="Количество машино-часов за рейс"
                          name="workHoursPerTrip"
                          required
                        >
                          {field => (
                            <InputNumber
                              name={field.name}
                              value={field.value ? convertFromSecondToHour(field.value) : ''}
                              min={0}
                              decimalSeparator=","
                              onChange={value => {
                                setFieldValue(field.name, convertFromHourToSecond(value));
                                setFieldValue(
                                  'vehicleMachineHoursAtEnd',
                                  Number(Number(vehicle.engineWorkHours ?? '0') + value).toFixed(2)
                                );
                              }}
                            />
                          )}
                        </FormField>
                      </GridItem>
                    </Grid>
                  </Content>
                </>
              )}
          </Section>
          <Footer>
            <Button
              disabled={isSubmitting}
              loading={isSubmitting}
              type="primary"
              htmlType="submit"
              className="login-form-button"
            >
              Таксировать
            </Button>
            <CancelButton dirty={dirty} onClick={() => navigate(`/trips/self`)}>
              Отменить
            </CancelButton>
          </Footer>
        </form>
      )}
    </Form>
  );
};

export default connect((state: AppState) => ({
  profile: state.auth.profile
}))(CreateTripListForm);
